export const forLawyers = {
  extends: 'Page',
  flow: 'y',
  background: 'surface',

  LandingNav: {},

  // ═══════════════════════════════════════════════════
  // HERO — simple, lawyer-focused
  // ═══════════════════════════════════════════════════
  HeroSection: {
    tag: 'section',
    flow: 'y',
    align: 'center',
    gap: 'B',
    padding: 'F D E D',
    minHeight: '60vh',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@tabletS': { padding: 'E B D B' },
    '@mobileL': { padding: 'D A C A' },

    HeroLabel: {
      text: 'FOR LAWYERS',
      fontSize: 'Y',
      fontWeight: '600',
      color: 'green',
      textTransform: 'uppercase',
      letterSpacing: '2px'
    },

    HeroTitle: {
      tag: 'h1',
      flow: 'y',
      gap: '0',
      align: 'center',
      TitleLine1: {
        text: 'Your practice.',
        fontSize: 'C1',
        fontWeight: '300',
        color: 'text',
        lineHeight: '1.15',
        letterSpacing: '-1px',
        textAlign: 'center',
        '@mobileL': { fontSize: 'C' }
      },
      TitleLine2: {
        text: 'Your clients.',
        fontSize: 'C1',
        fontWeight: '300',
        color: 'text',
        lineHeight: '1.15',
        letterSpacing: '-1px',
        textAlign: 'center',
        '@mobileL': { fontSize: 'C' }
      },
      TitleLine3: {
        text: 'One platform.',
        fontSize: 'C1',
        fontWeight: '700',
        color: 'green',
        lineHeight: '1.15',
        letterSpacing: '-1px',
        textAlign: 'center',
        '@mobileL': { fontSize: 'C' }
      }
    },

    HeroDesc: {
      text: 'Everything a modern lawyer needs — bookings, case management, AI-drafted documents, invoicing, and a client portal — in one clean dashboard.',
      fontSize: 'A',
      color: 'textMuted',
      lineHeight: '1.6',
      maxWidth: 'J',
      textAlign: 'center'
    },

    HeroCtas: {
      flow: 'x',
      gap: 'A',
      align: 'center center',
      '@mobileL': { flow: 'y', width: '100%' },
      TrialLink: {
        extends: 'Link',
        href: '/login',
        text: 'Start free trial',
        background: 'green',
        color: 'neutral950',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '600',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' },
        '@mobileL': { width: '100%', textAlign: 'center' }
      },
      DemoLink: {
        extends: 'Link',
        href: '/dashboard',
        text: 'See live demo',
        background: 'transparent',
        color: 'text',
        border: '1px solid surfaceBorderStrong',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '500',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' },
        onClick: (e, el, s) => s.root.update({ loggedIn: true }),
        '@mobileL': { width: '100%', textAlign: 'center' }
      }
    },

    HeroStats: {
      flow: 'x',
      gap: 'C',
      align: 'center center',
      padding: 'B 0 0 0',
      '@mobileL': { flow: 'y', gap: 'A' },
      Stat1: {
        flow: 'y',
        gap: 'Y',
        align: 'center',
        StatNum: {
          text: '500+',
          fontSize: 'C',
          fontWeight: '700',
          color: 'green',
          lineHeight: '1'
        },
        StatLabel: { text: 'Lawyers active', fontSize: 'Y', color: 'textMuted' }
      },
      StatDiv1: {
        width: '1px',
        height: 'B',
        background: 'surfaceBorderStrong',
        '@mobileL': { display: 'none' }
      },
      Stat2: {
        flow: 'y',
        gap: 'Y',
        align: 'center',
        StatNum: {
          text: '12k+',
          fontSize: 'C',
          fontWeight: '700',
          color: 'green',
          lineHeight: '1'
        },
        StatLabel: { text: 'Cases managed', fontSize: 'Y', color: 'textMuted' }
      },
      StatDiv2: {
        width: '1px',
        height: 'B',
        background: 'surfaceBorderStrong',
        '@mobileL': { display: 'none' }
      },
      Stat3: {
        flow: 'y',
        gap: 'Y',
        align: 'center',
        StatNum: {
          text: '98%',
          fontSize: 'C',
          fontWeight: '700',
          color: 'green',
          lineHeight: '1'
        },
        StatLabel: {
          text: 'Satisfaction rate',
          fontSize: 'Y',
          color: 'textMuted'
        }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // HOW IT WORKS
  // ═══════════════════════════════════════════════════
  HowSection: {
    tag: 'section',
    flow: 'y',
    gap: 'D',
    padding: 'F D',
    background: 'neutral50',
    color: 'neutral900',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    HowHeader: {
      flow: 'x',
      align: 'flex-start space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      HowTitle: {
        tag: 'h2',
        text: '{{ howItWorks | polyglot }}',
        fontSize: 'D',
        fontWeight: '700',
        color: 'neutral900',
        lineHeight: '1.1',
        '@mobileL': { fontSize: 'C' }
      },
      HowLabel: {
        text: '{{ processLabel | polyglot }}',
        fontSize: 'Y',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }
    },

    HowSteps: {
      flow: 'x',
      gap: 'D',
      '@mobileL': { flow: 'y', gap: 'C' },

      StepsList: {
        flow: 'y',
        gap: 'B',
        flex: '1',

        Step1: {
          flow: 'x',
          gap: 'A',
          align: 'flex-start',
          cursor: 'pointer',
          transition: 'opacity 150ms',
          opacity: (el, s) =>
            !s.root.flActiveStep || s.root.flActiveStep === 1 ? '1' : '.5',
          onClick: (e, el, s) => s.root.update({ flActiveStep: 1 }),
          StepMarker: {
            flow: 'x',
            gap: 'Y',
            align: 'center',
            StepDot: {
              width: 'Z',
              height: 'Z',
              background: (el, s) =>
                !s.root.flActiveStep || s.root.flActiveStep === 1
                  ? 'green'
                  : 'transparent',
              border: (el, s) =>
                !s.root.flActiveStep || s.root.flActiveStep === 1
                  ? 'none'
                  : '1px solid neutral300'
            },
            StepNum: {
              text: '01',
              fontSize: 'Y',
              color: (el, s) =>
                !s.root.flActiveStep || s.root.flActiveStep === 1
                  ? 'neutral500'
                  : 'neutral400',
              fontFamily: 'monospace'
            }
          },
          StepContent: {
            flow: 'y',
            gap: 'Y',
            StepTitle: {
              text: '{{ step1Title | polyglot }}',
              fontSize: 'A',
              fontWeight: '600',
              color: (el, s) =>
                !s.root.flActiveStep || s.root.flActiveStep === 1
                  ? 'neutral900'
                  : 'neutral500'
            },
            StepDesc: {
              text: '{{ step1Desc | polyglot }}',
              fontSize: 'Z',
              color: 'neutral500',
              lineHeight: '1.6'
            }
          }
        },
        Step2: {
          flow: 'x',
          gap: 'A',
          align: 'flex-start',
          cursor: 'pointer',
          transition: 'opacity 150ms',
          opacity: (el, s) => (s.root.flActiveStep === 2 ? '1' : '.5'),
          onClick: (e, el, s) => s.root.update({ flActiveStep: 2 }),
          StepMarker: {
            flow: 'x',
            gap: 'Y',
            align: 'center',
            StepDot: {
              width: 'Z',
              height: 'Z',
              background: (el, s) =>
                s.root.flActiveStep === 2 ? 'green' : 'transparent',
              border: (el, s) =>
                s.root.flActiveStep === 2 ? 'none' : '1px solid neutral300'
            },
            StepNum: {
              text: '02',
              fontSize: 'Y',
              color: (el, s) =>
                s.root.flActiveStep === 2 ? 'neutral500' : 'neutral400',
              fontFamily: 'monospace'
            }
          },
          StepContent: {
            flow: 'y',
            gap: 'Y',
            StepTitle: {
              text: '{{ step2Title | polyglot }}',
              fontSize: 'A',
              fontWeight: '600',
              color: (el, s) =>
                s.root.flActiveStep === 2 ? 'neutral900' : 'neutral500'
            },
            StepDesc: {
              text: '{{ step2Desc | polyglot }}',
              fontSize: 'Z',
              color: 'neutral400',
              lineHeight: '1.6'
            }
          }
        },
        Step3: {
          flow: 'x',
          gap: 'A',
          align: 'flex-start',
          cursor: 'pointer',
          transition: 'opacity 150ms',
          opacity: (el, s) => (s.root.flActiveStep === 3 ? '1' : '.5'),
          onClick: (e, el, s) => s.root.update({ flActiveStep: 3 }),
          StepMarker: {
            flow: 'x',
            gap: 'Y',
            align: 'center',
            StepDot: {
              width: 'Z',
              height: 'Z',
              background: (el, s) =>
                s.root.flActiveStep === 3 ? 'green' : 'transparent',
              border: (el, s) =>
                s.root.flActiveStep === 3 ? 'none' : '1px solid neutral300'
            },
            StepNum: {
              text: '03',
              fontSize: 'Y',
              color: (el, s) =>
                s.root.flActiveStep === 3 ? 'neutral500' : 'neutral400',
              fontFamily: 'monospace'
            }
          },
          StepContent: {
            flow: 'y',
            gap: 'Y',
            StepTitle: {
              text: '{{ step3Title | polyglot }}',
              fontSize: 'A',
              fontWeight: '600',
              color: (el, s) =>
                s.root.flActiveStep === 3 ? 'neutral900' : 'neutral500'
            },
            StepDesc: {
              text: '{{ step3Desc | polyglot }}',
              fontSize: 'Z',
              color: 'neutral400',
              lineHeight: '1.6'
            }
          }
        },
        Step4: {
          flow: 'x',
          gap: 'A',
          align: 'flex-start',
          cursor: 'pointer',
          transition: 'opacity 150ms',
          opacity: (el, s) => (s.root.flActiveStep === 4 ? '1' : '.5'),
          onClick: (e, el, s) => s.root.update({ flActiveStep: 4 }),
          StepMarker: {
            flow: 'x',
            gap: 'Y',
            align: 'center',
            StepDot: {
              width: 'Z',
              height: 'Z',
              background: (el, s) =>
                s.root.flActiveStep === 4 ? 'green' : 'transparent',
              border: (el, s) =>
                s.root.flActiveStep === 4 ? 'none' : '1px solid neutral300'
            },
            StepNum: {
              text: '04',
              fontSize: 'Y',
              color: (el, s) =>
                s.root.flActiveStep === 4 ? 'neutral500' : 'neutral400',
              fontFamily: 'monospace'
            }
          },
          StepContent: {
            flow: 'y',
            gap: 'Y',
            StepTitle: {
              text: '{{ step4Title | polyglot }}',
              fontSize: 'A',
              fontWeight: '600',
              color: (el, s) =>
                s.root.flActiveStep === 4 ? 'neutral900' : 'neutral500'
            },
            StepDesc: {
              text: '{{ step4Desc | polyglot }}',
              fontSize: 'Z',
              color: 'neutral400',
              lineHeight: '1.6'
            }
          }
        }
      },

      StepsDemo: {
        flow: 'y',
        flex: '1',
        background: 'neutral900',
        borderRadius: 'Z',
        padding: 'B',
        gap: 'A',
        transition: 'opacity 300ms',
        '@mobileL': { display: 'none' },

        Demo1: {
          flow: 'y',
          gap: 'A',
          show: (el, s) => !s.root.flActiveStep || s.root.flActiveStep === 1,
          DemoLabel: {
            text: '{{ demoBadge | polyglot }}',
            fontSize: 'Y',
            color: 'green',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          },
          DemoMockTitle: {
            text: '{{ todaysBookings | polyglot }}',
            fontSize: 'A',
            fontWeight: '600',
            color: 'neutral50'
          },
          DemoMock1: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: 'გიორგი მამულაშვილი',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockTime: {
              text: '10:00',
              fontSize: 'Z',
              color: 'green',
              fontWeight: '600'
            }
          },
          DemoMock2: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: 'მარიამ კვარაცხელია',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockTime: { text: '11:30', fontSize: 'Z', color: 'neutral400' }
          },
          DemoMock3: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            MockName: {
              text: 'დავით ჩიქოვანი',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockTime: { text: '14:00', fontSize: 'Z', color: 'neutral400' }
          }
        },
        Demo2: {
          flow: 'y',
          gap: 'A',
          show: (el, s) => s.root.flActiveStep === 2,
          DemoLabel: {
            text: '// საქმეები',
            fontSize: 'Y',
            color: 'green',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          },
          DemoMockTitle: {
            text: 'აქტიური საქმეები',
            fontSize: 'A',
            fontWeight: '600',
            color: 'neutral50'
          },
          DemoMock1: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: '#2024-047 — მამულაშვილი',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockStatus: {
              text: 'აქტიური',
              fontSize: 'Y',
              color: 'green',
              fontWeight: '600'
            }
          },
          DemoMock2: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: '#2024-046 — კვარაცხელია',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockStatus: {
              text: 'მოლოდინში',
              fontSize: 'Y',
              color: 'amber',
              fontWeight: '600'
            }
          },
          DemoMock3: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            MockName: {
              text: '#2024-045 — გოგიაშვილი',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockStatus: { text: 'დახურული', fontSize: 'Y', color: 'neutral400' }
          }
        },
        Demo3: {
          flow: 'y',
          gap: 'A',
          show: (el, s) => s.root.flActiveStep === 3,
          DemoLabel: {
            text: '// AI',
            fontSize: 'Y',
            color: 'green',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          },
          DemoMockTitle: {
            text: 'AI დოკუმენტები',
            fontSize: 'A',
            fontWeight: '600',
            color: 'neutral50'
          },
          DemoMock1: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: '📄 სარჩელი — გენერირებული',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockStatus: { text: '✓', fontSize: 'Z', color: 'green' }
          },
          DemoMock2: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: '📄 მინდობილობა — შაბლონი',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockStatus: { text: '✓', fontSize: 'Z', color: 'green' }
          },
          DemoMock3: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            MockName: {
              text: '🔍 OCR სკანირება — 98.5%',
              fontSize: 'Z',
              color: 'neutral50'
            },
            MockStatus: { text: '✓', fontSize: 'Z', color: 'green' }
          }
        },
        Demo4: {
          flow: 'y',
          gap: 'A',
          show: (el, s) => s.root.flActiveStep === 4,
          DemoLabel: {
            text: '// ანალიტიკა',
            fontSize: 'Y',
            color: 'green',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          },
          DemoMockTitle: {
            text: 'თვის შემოსავალი',
            fontSize: 'A',
            fontWeight: '600',
            color: 'neutral50'
          },
          DemoMock1: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: {
              text: 'სულ გაგზავნილი',
              fontSize: 'Z',
              color: 'neutral400'
            },
            MockVal: {
              text: '₾3,640',
              fontSize: 'Z',
              color: 'neutral50',
              fontWeight: '600'
            }
          },
          DemoMock2: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            borderBottom: '1px solid neutral700',
            MockName: { text: 'გადახდილი', fontSize: 'Z', color: 'neutral400' },
            MockVal: {
              text: '₾2,440',
              fontSize: 'Z',
              color: 'green',
              fontWeight: '600'
            }
          },
          DemoMock3: {
            flow: 'x',
            align: 'center space-between',
            padding: 'Z',
            MockName: {
              text: 'გადაუხდელი',
              fontSize: 'Z',
              color: 'neutral400'
            },
            MockVal: {
              text: '₾1,200',
              fontSize: 'Z',
              color: 'amber',
              fontWeight: '600'
            }
          }
        },

        DemoCta: {
          extends: 'Link',
          href: '/dashboard',
          text: '{{ fullDemo | polyglot }}',
          background: 'green',
          color: 'neutral950',
          padding: 'Z A',
          fontSize: 'Y',
          fontWeight: '600',
          borderRadius: '0',
          textDecoration: 'none',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          textAlign: 'center',
          onClick: (e, el, s) => s.root.update({ loggedIn: true })
        }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // FEATURES — three columns
  // ═══════════════════════════════════════════════════
  FeaturesSection: {
    tag: 'section',
    flow: 'y',
    gap: 'D',
    padding: 'F D',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    FeaturesHeader: {
      flow: 'x',
      align: 'flex-start space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      FeaturesTitle: {
        tag: 'h2',
        text: '{{ onePlatformThreePillars | polyglot }}',
        fontSize: 'D',
        fontWeight: '300',
        color: 'text',
        lineHeight: '1.15',
        whiteSpace: 'pre-wrap',
        '@mobileL': { fontSize: 'C' }
      },
      FeaturesLabel: {
        text: '{{ capabilitiesLabel | polyglot }}',
        fontSize: 'Y',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }
    },

    FeaturesRow: {
      flow: 'x',
      gap: 'A',
      '@mobileL': { flow: 'y' },

      FeatureBooking: {
        flow: 'y',
        gap: 'A',
        flex: '1',
        padding: 'B',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        transition: 'border-color 200ms',
        ':hover': { borderColor: 'green' },
        FeatureNum: {
          text: '01',
          fontSize: 'Y',
          color: 'green',
          fontFamily: 'monospace'
        },
        FeatureName: {
          tag: 'h3',
          text: '{{ bookingFeature | polyglot }}',
          fontSize: 'B',
          fontWeight: '600',
          color: 'text'
        },
        FeatureDesc: {
          text: '{{ bookingFeatureDesc | polyglot }}',
          fontSize: 'Z',
          color: 'textMuted',
          lineHeight: '1.7'
        }
      },
      FeatureOps: {
        flow: 'y',
        gap: 'A',
        flex: '1',
        padding: 'B',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        transition: 'border-color 200ms',
        ':hover': { borderColor: 'green' },
        FeatureNum: {
          text: '02',
          fontSize: 'Y',
          color: 'green',
          fontFamily: 'monospace'
        },
        FeatureName: {
          tag: 'h3',
          text: '{{ operationsFeature | polyglot }}',
          fontSize: 'B',
          fontWeight: '600',
          color: 'text'
        },
        FeatureDesc: {
          text: '{{ operationsFeatureDesc | polyglot }}',
          fontSize: 'Z',
          color: 'textMuted',
          lineHeight: '1.7'
        }
      },
      FeatureAI: {
        flow: 'y',
        gap: 'A',
        flex: '1',
        padding: 'B',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        borderRadius: 'Z',
        transition: 'border-color 200ms',
        ':hover': { borderColor: 'green' },
        FeatureNum: {
          text: '03',
          fontSize: 'Y',
          color: 'green',
          fontFamily: 'monospace'
        },
        FeatureName: {
          tag: 'h3',
          text: '{{ aiFeature | polyglot }}',
          fontSize: 'B',
          fontWeight: '600',
          color: 'text'
        },
        FeatureDesc: {
          text: '{{ aiFeatureDesc | polyglot }}',
          fontSize: 'Z',
          color: 'textMuted',
          lineHeight: '1.7'
        }
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // PRICING
  // ═══════════════════════════════════════════════════
  PricingSection: {
    tag: 'section',
    flow: 'y',
    gap: 'D',
    padding: 'F D',
    background: 'neutral50',
    color: 'neutral900',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    PricingHeader: {
      flow: 'x',
      align: 'flex-start space-between',
      '@mobileL': { flow: 'y', gap: 'A' },
      PricingTitle: {
        tag: 'h2',
        text: '{{ simplePricing | polyglot }}',
        fontSize: 'D',
        fontWeight: '700',
        color: 'neutral900',
        lineHeight: '1.1',
        '@mobileL': { fontSize: 'C' }
      },
      PricingLabel: {
        text: '{{ plansLabel | polyglot }}',
        fontSize: 'Y',
        fontWeight: '600',
        color: 'green',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }
    },

    PricingRow: {
      flow: 'x',
      gap: 'A',
      '@mobileL': { flow: 'y' },

      PriceTier1: {
        flow: 'y',
        gap: 'A',
        flex: '1',
        padding: 'B',
        background: 'neutral900',
        color: 'neutral50',
        borderRadius: 'Z',
        PriceAmount: {
          text: '₾15',
          fontSize: 'C',
          fontWeight: '300',
          color: 'neutral50',
          lineHeight: '1'
        },
        PricePeriod: {
          text: '{{ perMonth | polyglot }}',
          fontSize: 'Z',
          color: 'neutral400'
        },
        PriceFeatures: {
          flow: 'y',
          gap: '0',
          padding: 'A 0 0 0',
          PriceFeat1: {
            text: '{{ bookingMgmt | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat2: {
            text: '{{ caseTracking | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat3: {
            text: '{{ calendarSync | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat4: {
            text: '{{ emailReminders | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0'
          }
        },
        PriceLabel: {
          text: '{{ starterPlan | polyglot }}',
          fontSize: 'Y',
          color: 'neutral400',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          padding: 'A 0 0 0'
        },
        PriceCta: {
          extends: 'Link',
          href: '/login',
          text: '{{ startNow | polyglot }}',
          background: 'neutral50',
          color: 'neutral900',
          padding: 'Z A',
          fontSize: 'Y',
          fontWeight: '600',
          textDecoration: 'none',
          textAlign: 'center',
          width: '100%'
        }
      },

      PriceTier2: {
        flow: 'y',
        gap: 'A',
        flex: '1',
        padding: 'B',
        background: 'neutral900',
        color: 'neutral50',
        borderRadius: 'Z',
        border: '2px solid green',
        PriceAmount: {
          text: '₾75',
          fontSize: 'C',
          fontWeight: '300',
          color: 'green',
          lineHeight: '1'
        },
        PricePeriod: {
          text: '{{ perMonth | polyglot }}',
          fontSize: 'Z',
          color: 'neutral400'
        },
        PriceFeatures: {
          flow: 'y',
          gap: '0',
          padding: 'A 0 0 0',
          PriceFeat1: {
            text: '{{ everythingStarter | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat2: {
            text: '{{ aiDocDrafting | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat3: {
            text: '{{ invoicesPayments | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat4: {
            text: '{{ clientPortalFeature | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat5: {
            text: '{{ directSupport | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0'
          }
        },
        PriceLabel: {
          text: '{{ professionalPlan | polyglot }}',
          fontSize: 'Y',
          color: 'neutral400',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          padding: 'A 0 0 0'
        },
        PriceAvail: {
          flow: 'x',
          gap: 'Y',
          align: 'center',
          AvailDot: { width: 'Z', height: 'Z', background: 'green' },
          AvailText: {
            text: '{{ mostPopular | polyglot }}',
            fontSize: 'Y',
            color: 'green',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }
        },
        PriceCta: {
          extends: 'Link',
          href: '/login',
          text: '{{ startNow | polyglot }}',
          background: 'green',
          color: 'neutral950',
          padding: 'Z A',
          fontSize: 'Y',
          fontWeight: '600',
          textDecoration: 'none',
          textAlign: 'center',
          width: '100%'
        }
      },

      PriceTier3: {
        flow: 'y',
        gap: 'A',
        flex: '1',
        padding: 'B',
        background: 'neutral900',
        color: 'neutral50',
        borderRadius: 'Z',
        PriceAmount: {
          text: '₾299',
          fontSize: 'C',
          fontWeight: '300',
          color: 'neutral50',
          lineHeight: '1'
        },
        PricePeriod: {
          text: '{{ perMonth | polyglot }}',
          fontSize: 'Z',
          color: 'neutral400'
        },
        PriceFeatures: {
          flow: 'y',
          gap: '0',
          padding: 'A 0 0 0',
          PriceFeat1: {
            text: '{{ everythingPro | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat2: {
            text: '{{ teamMgmt10 | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat3: {
            text: '{{ contractAnalysis | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat4: {
            text: '{{ advancedAnalytics | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0',
            borderBottom: '1px solid neutral700'
          },
          PriceFeat5: {
            text: '{{ personalOnboarding | polyglot }}',
            fontSize: 'Z',
            color: 'neutral300',
            padding: 'Z 0'
          }
        },
        PriceLabel: {
          text: '{{ businessPlan | polyglot }}',
          fontSize: 'Y',
          color: 'neutral400',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          padding: 'A 0 0 0'
        },
        PriceAvail: {
          flow: 'x',
          gap: 'Y',
          align: 'center',
          AvailDot: { width: 'Z', height: 'Z', background: 'green' },
          AvailText: {
            text: '{{ spotsLeft | polyglot }}',
            fontSize: 'Y',
            color: 'green',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }
        },
        PriceCta: {
          extends: 'Link',
          href: '/login',
          text: '{{ startNow | polyglot }}',
          background: 'neutral50',
          color: 'neutral900',
          padding: 'Z A',
          fontSize: 'Y',
          fontWeight: '600',
          textDecoration: 'none',
          textAlign: 'center',
          width: '100%'
        }
      }
    },

    PricingNote: {
      text: '{{ enterpriseNote | polyglot }}',
      fontSize: 'Z',
      color: 'neutral500',
      textAlign: 'center',
      padding: 'A 0 0 0'
    }
  },

  // ═══════════════════════════════════════════════════
  // CTA
  // ═══════════════════════════════════════════════════
  CtaBanner: {
    tag: 'section',
    flow: 'y',
    align: 'center center',
    padding: 'F D',
    gap: 'B',
    textAlign: 'center',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    CtaTitle: {
      tag: 'h2',
      text: '{{ readyQuestion | polyglot }}',
      fontSize: 'D',
      fontWeight: '700',
      color: 'text',
      '@mobileL': { fontSize: 'C' }
    },
    CtaSub: {
      text: '{{ first30DaysFree | polyglot }}',
      fontSize: 'A',
      color: 'textMuted',
      maxWidth: 'H'
    },
    CtaCtas: {
      flow: 'x',
      gap: 'A',
      align: 'center',
      CtaBtn: {
        extends: 'Link',
        href: '/login',
        text: '{{ tryFree | polyglot }}',
        background: 'green',
        color: 'neutral950',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '600',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' }
      },
      DemoBtn: {
        extends: 'Link',
        href: '/dashboard',
        text: '{{ liveDemo | polyglot }}',
        background: 'transparent',
        color: 'text',
        border: '1px solid surfaceBorderStrong',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '500',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' },
        onClick: (e, el, s) => s.root.update({ loggedIn: true })
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // FOOTER
  // ═══════════════════════════════════════════════════
  FooterSection: {
    tag: 'footer',
    flow: 'y',
    gap: 'C',
    padding: 'D 0',
    borderTop: '1px solid surfaceBorderStrong',
    maxWidth: '1680px',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'C A' },

    FooterTop: {
      flow: 'x',
      gap: 'C',
      '@mobileL': { flow: 'y', gap: 'B' },
      FooterBrand: {
        flow: 'y',
        gap: 'A',
        flex: '1.5',
        minWidth: 'F',
        FooterLogo: {
          extends: 'Link',
          href: '/',
          text: 'TrustLegal',
          fontSize: 'B',
          fontWeight: '800',
          color: 'green',
          textDecoration: 'none'
        },
        FooterTagline: {
          text: '{{ footerTagline | polyglot }}',
          fontSize: 'Z',
          color: 'textMuted',
          lineHeight: '1.6'
        }
      },
      FooterLinks: {
        flow: 'x',
        gap: 'C',
        flex: '2',
        '@mobileL': { flow: 'y', gap: 'B' },
        FooterCol1: {
          flow: 'y',
          gap: 'Z',
          flex: '1',
          ColTitle: {
            text: '{{ productLabel | polyglot }}',
            fontSize: 'Y',
            fontWeight: '600',
            color: 'textMuted',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            padding: '0 0 Z 0'
          },
          ColLink1: {
            extends: 'Link',
            href: '/bookings',
            text: '{{ bookingsLink | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' },
            onClick: (e, el, s) => s.root.update({ loggedIn: true })
          },
          ColLink2: {
            extends: 'Link',
            href: '/cases',
            text: '{{ casesLink | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' },
            onClick: (e, el, s) => s.root.update({ loggedIn: true })
          },
          ColLink3: {
            extends: 'Link',
            href: '/documents',
            text: '{{ aiDocsLink | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' },
            onClick: (e, el, s) => s.root.update({ loggedIn: true })
          }
        },
        FooterCol2: {
          flow: 'y',
          gap: 'Z',
          flex: '1',
          ColTitle: {
            text: '{{ companyLabel | polyglot }}',
            fontSize: 'Y',
            fontWeight: '600',
            color: 'textMuted',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            padding: '0 0 Z 0'
          },
          ColLink1: {
            extends: 'Link',
            href: '/',
            text: '{{ aboutUs | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' }
          },
          ColLink2: {
            extends: 'Link',
            href: '/',
            text: '{{ careers | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' }
          }
        }
      }
    },
    FooterBottom: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A 0 0 0',
      borderTop: '1px solid surfaceBorderStrong',
      '@mobileL': { flow: 'y', gap: 'Z', align: 'center center' },
      CopyText: {
        text: '{{ copyrightText | polyglot }}',
        fontSize: 'Z',
        color: 'textMuted'
      },
      BuiltWith: {
        text: '{{ builtWith | polyglot }}',
        fontSize: 'Z',
        color: 'textMuted'
      }
    }
  }
}
